import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { registrationFieldsFor, sanitizeInailPin, INAIL_PIN_MAX_DIGITS } from "@/foundation/anagrafica/istituti";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, X, Shield, Building, Landmark, Calculator } from "lucide-react";
import { SuiteCard, SuiteButton } from "@/components/suite";
import { DurationInput, DurationLabel } from "@/foundation/duration";

/**
 * Pannello Contratti e Istituti — multi CCNL + iscrizioni istituzionali.
 * v0.99.85+: aggiunte le sezioni "Totali annui" (informativi) e sostituiti i
 * form inline con `Dialog` shadcn: chiusura tramite X, tasto Annulla, Escape
 * e click all'esterno sono ora gestiti nativamente da Radix.
 */

const KIND_LABEL = {
  inps: "INPS",
  inail: "INAIL",
  cassa_edile: "Cassa Edile",
  altro: "Altro",
};
const KIND_ICON = {
  inps: Landmark,
  inail: Shield,
  cassa_edile: Building,
  altro: Landmark,
};

const EMPTY_CCNL = {
  name: "", cnel_code: "", start_date: "", end_date: "",
  is_primary: false, active: true,
  ferie_total_annual: "", ferie_unit: "days",
  rol_total_annual_hours: "", ex_festivita_total_annual_hours: "",
  notes: "",
};
const EMPTY_REG = { kind: "inps", number: "", pat: "", pin: "", ccnl_id: "", start_date: "", end_date: "", active: true, notes: "" };

function normalizeAccrual(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n) || n < 0) return "__invalid__";
  return n;
}

export default function CompanyContractsPanel({ companyId, readOnly = false }) {
  const [ccnl, setCcnl] = useState([]);
  const [regs, setRegs] = useState([]);
  const [ccnlForm, setCcnlForm] = useState(EMPTY_CCNL);
  const [ccnlEditingId, setCcnlEditingId] = useState(null);
  const [ccnlOpen, setCcnlOpen] = useState(false);
  const [regForm, setRegForm] = useState(EMPTY_REG);
  const regFields = registrationFieldsFor(regForm.kind);
  const [regEditingId, setRegEditingId] = useState(null);
  const [regOpen, setRegOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [divisorsCcnl, setDivisorsCcnl] = useState(null); // CCNL corrente per gestione divisori
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get(`/companies/${companyId}/ccnl`),
        api.get(`/companies/${companyId}/registrations`),
      ]);
      setCcnl(c.data || []);
      setRegs(r.data || []);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // --- CCNL ---
  const startNewCcnl = () => { setCcnlForm(EMPTY_CCNL); setCcnlEditingId(null); setCcnlOpen(true); };
  const editCcnl = (c) => {
    setCcnlForm({
      name: c.name || "", cnel_code: c.cnel_code || "",
      start_date: c.start_date || "", end_date: c.end_date || "",
      is_primary: !!c.is_primary, active: c.active !== false,
      ferie_total_annual: c.ferie_total_annual ?? "",
      ferie_unit: c.ferie_unit || "days",
      rol_total_annual_hours: c.rol_total_annual_hours ?? "",
      ex_festivita_total_annual_hours: c.ex_festivita_total_annual_hours ?? "",
      notes: c.notes || "",
    });
    setCcnlEditingId(c.id); setCcnlOpen(true);
  };
  const saveCcnl = async () => {
    if (!ccnlForm.name?.trim()) { toast.error("Denominazione CCNL obbligatoria"); return; }
    // Validazione accrual: non negativi.
    const ferie = normalizeAccrual(ccnlForm.ferie_total_annual);
    const rol = normalizeAccrual(ccnlForm.rol_total_annual_hours);
    const exf = normalizeAccrual(ccnlForm.ex_festivita_total_annual_hours);
    for (const [label, val] of [["Ferie tot. annuo", ferie], ["ROL/Permessi tot. annuo", rol], ["Ex Festivita' tot. annuo", exf]]) {
      if (val === "__invalid__") { toast.error(`${label}: valore non valido (deve essere >= 0)`); return; }
    }
    const payload = {
      ...ccnlForm,
      ferie_total_annual: ferie,
      ferie_unit: ferie === null ? null : (ccnlForm.ferie_unit || "days"),
      rol_total_annual_hours: rol,
      ex_festivita_total_annual_hours: exf,
    };
    setSaving(true);
    try {
      if (ccnlEditingId) {
        await api.patch(`/companies/${companyId}/ccnl/${ccnlEditingId}`, payload);
        toast.success("CCNL aggiornato");
      } else {
        await api.post(`/companies/${companyId}/ccnl`, payload);
        toast.success("CCNL aggiunto");
      }
      setCcnlOpen(false); setCcnlEditingId(null); setCcnlForm(EMPTY_CCNL);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const deleteCcnl = async (c) => {
    if (!window.confirm(`Eliminare il CCNL "${c.name}"? Le iscrizioni associate perderanno il riferimento.`)) return;
    try { await api.delete(`/companies/${companyId}/ccnl/${c.id}`); toast.success("CCNL eliminato"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // --- Registrations ---
  const startNewReg = () => { setRegForm(EMPTY_REG); setRegEditingId(null); setRegOpen(true); };
  const editReg = (r) => {
    setRegForm({
      kind: r.kind || "inps", number: r.number || "", pat: r.pat || "", pin: r.pin || "",
      ccnl_id: r.ccnl_id || "", start_date: r.start_date || "", end_date: r.end_date || "",
      active: r.active !== false, notes: r.notes || "",
    });
    setRegEditingId(r.id); setRegOpen(true);
  };
  const saveReg = async () => {
    setSaving(true);
    try {
      const payload = { ...regForm, ccnl_id: regForm.ccnl_id || null, pin: regFields.showPin ? (regForm.pin || null) : null };
      if (regEditingId) {
        await api.patch(`/companies/${companyId}/registrations/${regEditingId}`, payload);
        toast.success("Iscrizione aggiornata");
      } else {
        await api.post(`/companies/${companyId}/registrations`, payload);
        toast.success("Iscrizione aggiunta");
      }
      setRegOpen(false); setRegEditingId(null); setRegForm(EMPTY_REG);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const deleteReg = async (r) => {
    if (!window.confirm(`Eliminare l'iscrizione ${KIND_LABEL[r.kind] || r.kind}${r.number ? ` (${r.number})` : ""}?`)) return;
    try { await api.delete(`/companies/${companyId}/registrations/${r.id}`); toast.success("Iscrizione eliminata"); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!companyId) {
    return (
      <div className="text-sm text-slate-500 px-3 py-6">
        Salva prima l&apos;anagrafica dell&apos;azienda per poter configurare contratti e iscrizioni.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ===== CCNL ===== */}
      <SuiteCard padding="sm" testid="ccnl-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Contratti collettivi (CCNL)</h3>
            <p className="text-[11px] text-slate-500">Puoi registrare piu&apos; CCNL con relativa decorrenza. Il CCNL principale viene evidenziato. Per ogni CCNL puoi gestire lo storico dei <b>divisori contrattuali</b> (orario e giornaliero mensile) tramite il pulsante <b>Divisori</b> a fine riga.</p>
          </div>
          {!readOnly && (
            <SuiteButton size="sm" icon={Plus} onClick={startNewCcnl} data-testid="add-ccnl-btn">Aggiungi CCNL</SuiteButton>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Caricamento…</div>
        ) : ccnl.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-4 text-center">
            Nessun CCNL configurato.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ccnl-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-1.5">Denominazione</th>
                  <th className="px-2 py-1.5">Cod. CNEL</th>
                  <th className="px-2 py-1.5">Dal</th>
                  <th className="px-2 py-1.5">Al</th>
                  <th className="px-2 py-1.5">Ferie/annuo</th>
                  <th className="px-2 py-1.5">ROL h/annuo</th>
                  <th className="px-2 py-1.5">Ex Fest. h/annuo</th>
                  <th className="px-2 py-1.5">Principale</th>
                  <th className="px-2 py-1.5">Stato</th>
                  {!readOnly && <th className="px-2 py-1.5 text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {ccnl.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`ccnl-row-${c.id}`}>
                    <td className="px-2 py-1.5 font-medium text-slate-800">{c.name}</td>
                    <td className="px-2 py-1.5 font-mono text-[12px] text-slate-600">{c.cnel_code || "-"}</td>
                    <td className="px-2 py-1.5">{c.start_date || "-"}</td>
                    <td className="px-2 py-1.5">{c.end_date || "-"}</td>
                    <td className="px-2 py-1.5 text-slate-600">
                      {c.ferie_total_annual != null ? `${c.ferie_total_annual} ${c.ferie_unit === "hours" ? "h" : "gg"}` : "-"}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">{c.rol_total_annual_hours != null ? `${c.rol_total_annual_hours} h` : "-"}</td>
                    <td className="px-2 py-1.5 text-slate-600">{c.ex_festivita_total_annual_hours != null ? `${c.ex_festivita_total_annual_hours} h` : "-"}</td>
                    <td className="px-2 py-1.5">
                      {c.is_primary && <span className="px-1.5 py-0.5 rounded text-[11px] bg-suite-primary/10 text-suite-primary">Principale</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {c.active ? "Attivo" : "Sospeso"}
                      </span>
                    </td>
                    {!readOnly && (
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDivisorsCcnl(c)}
                          data-testid={`divisors-ccnl-${c.id}`}
                          title="Gestione divisori contrattuali storicizzati (orario / giornaliero mensile)"
                          className="mr-1 h-7 px-2 text-[11px] border-slate-300 hover:bg-suite-primary/5 hover:text-suite-primary hover:border-suite-primary/40"
                        >
                          <Calculator size={12} className="mr-1" /> Divisori
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => editCcnl(c)} data-testid={`edit-ccnl-${c.id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCcnl(c)} className="text-red-700" data-testid={`delete-ccnl-${c.id}`}><Trash2 size={14} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Dialog CCNL — formDialog: chiusura SOLO via X / Annulla / Salva (successo).
            ESC, click esterno e cambio focus NON chiudono il dialog e non alterano i dati. */}
        <Dialog open={ccnlOpen} onOpenChange={setCcnlOpen}>
          <DialogContent
            formDialog
            className="max-w-2xl"
            data-testid="ccnl-dialog"
          >
            <DialogHeader>
              <DialogTitle>{ccnlEditingId ? "Modifica CCNL" : "Aggiungi CCNL"}</DialogTitle>
              <DialogDescription>Definisci il contratto collettivo e i totali annui contrattuali (informativi).</DialogDescription>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-3 py-2 max-h-[70vh] overflow-y-auto">
              <div className="md:col-span-2">
                <Label>Denominazione CCNL *</Label>
                <Input value={ccnlForm.name} onChange={(e) => setCcnlForm({ ...ccnlForm, name: e.target.value })} placeholder="es. CCNL Studi Professionali" data-testid="ccnl-form-name" />
              </div>
              <div><Label>Codice CNEL</Label><Input value={ccnlForm.cnel_code} onChange={(e) => setCcnlForm({ ...ccnlForm, cnel_code: e.target.value })} placeholder="es. Y015" data-testid="ccnl-form-cnel" /></div>
              <div><Label>Data inizio</Label><Input type="date" value={ccnlForm.start_date} onChange={(e) => setCcnlForm({ ...ccnlForm, start_date: e.target.value })} /></div>
              <div><Label>Data fine</Label><Input type="date" value={ccnlForm.end_date} onChange={(e) => setCcnlForm({ ...ccnlForm, end_date: e.target.value })} /></div>
              <div className="flex items-center gap-4 md:col-span-2">
                <label className="flex items-center gap-2"><Switch checked={ccnlForm.is_primary} onCheckedChange={(v) => setCcnlForm({ ...ccnlForm, is_primary: v })} data-testid="ccnl-form-primary" /><span className="text-sm">Principale</span></label>
                <label className="flex items-center gap-2"><Switch checked={ccnlForm.active} onCheckedChange={(v) => setCcnlForm({ ...ccnlForm, active: v })} data-testid="ccnl-form-active" /><span className="text-sm">Attivo</span></label>
              </div>

              {/* Sezione totali annui contrattuali (informativi) */}
              <div className="md:col-span-2 border-t border-slate-200 pt-3 mt-1">
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Totali annui contrattuali (informativi)</h4>
                <p className="text-[11px] text-slate-500 mb-3">Valori di riferimento del CCNL: non modificano ratei ne&apos; calcoli automatici dei dipendenti.</p>
              </div>
              <div>
                {ccnlForm.ferie_unit === "hours" ? (
                  <>
                    <DurationLabel data-testid="ccnl-form-ferie-total">Ferie tot. annuo</DurationLabel>
                    <DurationInput value={ccnlForm.ferie_total_annual}
                      onCommit={(n) => setCcnlForm((f) => ({ ...f, ferie_total_annual: n ?? "" }))}
                      data-testid="ccnl-form-ferie-total" />
                  </>
                ) : (
                  <>
                    <Label>Ferie tot. annuo</Label>
                    <Input type="number" min="0" step="0.01"
                      value={ccnlForm.ferie_total_annual}
                      onChange={(e) => setCcnlForm({ ...ccnlForm, ferie_total_annual: e.target.value })}
                      placeholder="es. 26"
                      data-testid="ccnl-form-ferie-total" />
                  </>
                )}
              </div>
              <div>
                <Label>Unita&apos; ferie</Label>
                <Select value={ccnlForm.ferie_unit || "days"} onValueChange={(v) => setCcnlForm({ ...ccnlForm, ferie_unit: v })}>
                  <SelectTrigger data-testid="ccnl-form-ferie-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Giorni</SelectItem>
                    <SelectItem value="hours">Ore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <DurationLabel data-testid="ccnl-form-rol-total">ROL/Permessi tot. annuo (ore)</DurationLabel>
                <DurationInput value={ccnlForm.rol_total_annual_hours}
                  onCommit={(n) => setCcnlForm((f) => ({ ...f, rol_total_annual_hours: n ?? "" }))}
                  data-testid="ccnl-form-rol-total" />
              </div>
              <div>
                <DurationLabel data-testid="ccnl-form-exfest-total">Ex Festivita&apos; tot. annuo (ore)</DurationLabel>
                <DurationInput value={ccnlForm.ex_festivita_total_annual_hours}
                  onCommit={(n) => setCcnlForm((f) => ({ ...f, ex_festivita_total_annual_hours: n ?? "" }))}
                  data-testid="ccnl-form-exfest-total" />
              </div>

              <div className="md:col-span-2"><Label>Note</Label><Textarea value={ccnlForm.notes} onChange={(e) => setCcnlForm({ ...ccnlForm, notes: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCcnlOpen(false)} data-testid="ccnl-cancel"><X size={14} className="mr-1" /> Annulla</Button>
              <SuiteButton onClick={saveCcnl} icon={Save} loading={saving} data-testid="ccnl-save">
                {ccnlEditingId ? "Aggiorna" : "Aggiungi"}
              </SuiteButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SuiteCard>

      {/* ===== Iscrizioni istituzionali ===== */}
      <SuiteCard padding="sm" testid="registrations-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Iscrizioni istituzionali</h3>
            <p className="text-[11px] text-slate-500">INPS, INAIL/PAT, Cassa Edile e altri istituti. Puoi associare facoltativamente un CCNL.</p>
          </div>
          {!readOnly && (
            <SuiteButton size="sm" icon={Plus} onClick={startNewReg} data-testid="add-registration-btn">Aggiungi iscrizione</SuiteButton>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Caricamento…</div>
        ) : regs.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-4 text-center">
            Nessuna iscrizione istituzionale configurata.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="registrations-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-1.5">Tipo</th>
                  <th className="px-2 py-1.5">Numero / Codice Ditta</th>
                  <th className="px-2 py-1.5">PAT</th>
                  <th className="px-2 py-1.5">Codice PIN</th>
                  <th className="px-2 py-1.5">CCNL associato</th>
                  <th className="px-2 py-1.5">Dal</th>
                  <th className="px-2 py-1.5">Al</th>
                  <th className="px-2 py-1.5">Stato</th>
                  {!readOnly && <th className="px-2 py-1.5 text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {regs.map((r) => {
                  const Icon = KIND_ICON[r.kind] || Landmark;
                  const cLink = ccnl.find((x) => x.id === r.ccnl_id);
                  return (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`reg-row-${r.id}`}>
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center gap-1 text-slate-700"><Icon size={12} /> {KIND_LABEL[r.kind] || r.kind}</span>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[12px] text-slate-600">{r.number || "-"}</td>
                      <td className="px-2 py-1.5 font-mono text-[12px] text-slate-600">{r.pat || "-"}</td>
                      <td className="px-2 py-1.5 font-mono text-[12px] text-slate-600" data-testid={`reg-row-pin-${r.id}`}>{(r.kind === "inail" && r.pin) || "-"}</td>
                      <td className="px-2 py-1.5">{cLink?.name || "-"}</td>
                      <td className="px-2 py-1.5">{r.start_date || "-"}</td>
                      <td className="px-2 py-1.5">{r.end_date || "-"}</td>
                      <td className="px-2 py-1.5">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {r.active ? "Attiva" : "Sospesa"}
                        </span>
                      </td>
                      {!readOnly && (
                        <td className="px-2 py-1.5 text-right">
                          <Button size="sm" variant="ghost" onClick={() => editReg(r)} data-testid={`edit-reg-${r.id}`}><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteReg(r)} className="text-red-700" data-testid={`delete-reg-${r.id}`}><Trash2 size={14} /></Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={regOpen} onOpenChange={setRegOpen}>
          <DialogContent
            formDialog
            className="max-w-2xl"
            data-testid="reg-dialog"
          >
            <DialogHeader>
              <DialogTitle>{regEditingId ? "Modifica iscrizione" : "Aggiungi iscrizione"}</DialogTitle>
              <DialogDescription>Configura l&apos;iscrizione istituzionale (INPS, INAIL/PAT, Cassa Edile o altro).</DialogDescription>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-3 py-2 max-h-[70vh] overflow-y-auto">
              <div>
                <Label>Tipo *</Label>
                <Select value={regForm.kind} onValueChange={(v) => setRegForm({ ...regForm, kind: v })}>
                  <SelectTrigger data-testid="reg-form-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inps">INPS</SelectItem>
                    <SelectItem value="inail">INAIL</SelectItem>
                    <SelectItem value="cassa_edile">Cassa Edile</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{regFields.numberLabel}</Label><Input value={regForm.number} onChange={(e) => setRegForm({ ...regForm, number: e.target.value })} placeholder="es. 1234567890" data-testid="reg-form-number" /></div>
              {regFields.showPat && (
                <div><Label>PAT (INAIL)</Label><Input value={regForm.pat} onChange={(e) => setRegForm({ ...regForm, pat: e.target.value })} placeholder="es. 987654321/00" data-testid="reg-form-pat" /></div>
              )}
              {regFields.showPin && (
                <div>
                  <Label htmlFor="reg-form-pin">Codice PIN</Label>
                  <Input id="reg-form-pin" value={regForm.pin} inputMode="numeric" maxLength={INAIL_PIN_MAX_DIGITS} autoComplete="off"
                    onChange={(e) => setRegForm({ ...regForm, pin: sanitizeInailPin(e.target.value) })}
                    placeholder="es. 123456" data-testid="reg-form-pin" />
                </div>
              )}
              <div>
                <Label>CCNL associato (facoltativo)</Label>
                <Select value={regForm.ccnl_id || "__none__"} onValueChange={(v) => setRegForm({ ...regForm, ccnl_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="reg-form-ccnl"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuno</SelectItem>
                    {ccnl.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Data inizio</Label><Input type="date" value={regForm.start_date} onChange={(e) => setRegForm({ ...regForm, start_date: e.target.value })} /></div>
              <div><Label>Data fine</Label><Input type="date" value={regForm.end_date} onChange={(e) => setRegForm({ ...regForm, end_date: e.target.value })} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={regForm.active} onCheckedChange={(v) => setRegForm({ ...regForm, active: v })} data-testid="reg-form-active" />
                <span className="text-sm">Attiva</span>
              </div>
              <div className="md:col-span-2"><Label>Note</Label><Textarea value={regForm.notes} onChange={(e) => setRegForm({ ...regForm, notes: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRegOpen(false)} data-testid="reg-cancel"><X size={14} className="mr-1" /> Annulla</Button>
              <SuiteButton onClick={saveReg} icon={Save} loading={saving} data-testid="reg-save">
                {regEditingId ? "Aggiorna" : "Aggiungi"}
              </SuiteButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SuiteCard>
      <DivisorsDialog ccnl={divisorsCcnl} onClose={() => setDivisorsCcnl(null)} />
    </div>
  );
}


function DivisorsDialog({ ccnl, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ divisore_orario_mensile: "", divisore_giornaliero_mensile: "", effective_from: "", notes: "" });
  const reload = useCallback(async () => {
    if (!ccnl) return;
    setLoading(true);
    try {
      const r = await api.get(`/ccnl/${ccnl.id}/divisors`);
      setRows(r.data.items || []);
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, [ccnl]);
  useEffect(() => { if (ccnl) reload(); }, [ccnl, reload]);
  const save = async () => {
    const oh = parseFloat(form.divisore_orario_mensile);
    const dh = parseFloat(form.divisore_giornaliero_mensile);
    if (!(oh > 0) || !(dh > 0)) { toast.error("Divisori devono essere numeri positivi"); return; }
    if (!form.effective_from) { toast.error("Indicare la decorrenza"); return; }
    try {
      await api.post(`/ccnl/${ccnl.id}/divisors`, {
        divisore_orario_mensile: oh,
        divisore_giornaliero_mensile: dh,
        effective_from: form.effective_from,
        notes: form.notes || null,
      });
      toast.success("Divisori salvati");
      setForm({ divisore_orario_mensile: "", divisore_giornaliero_mensile: "", effective_from: "", notes: "" });
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!ccnl) return null;
  return (
    <Dialog open={!!ccnl} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent formDialog className="max-w-2xl" data-testid="divisors-dialog">
        <DialogHeader>
          <DialogTitle>Divisori CCNL — {ccnl.name}</DialogTitle>
          <DialogDescription>Divisori contrattuali storicizzati per decorrenza. Ogni divisore vale per il periodo dalla sua decorrenza fino alla successiva. Ogni elaborazione usa il divisore valido nella data di competenza.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>Divisore orario mensile *</Label><Input type="number" step="0.01" min="0" value={form.divisore_orario_mensile} onChange={(e) => setForm({ ...form, divisore_orario_mensile: e.target.value })} placeholder="es. 170" data-testid="divisor-hour" /></div>
            <div><Label>Divisore giornaliero mensile *</Label><Input type="number" step="0.01" min="0" value={form.divisore_giornaliero_mensile} onChange={(e) => setForm({ ...form, divisore_giornaliero_mensile: e.target.value })} placeholder="es. 26" data-testid="divisor-day" /></div>
            <div><Label>Decorrenza *</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="divisor-from" /></div>
            <div className="flex items-end"><SuiteButton onClick={save} data-testid="divisor-save"><Plus size={14} className="mr-1" />Aggiungi</SuiteButton></div>
          </div>
          <div className="border-t pt-3">
            <div className="text-xs text-slate-600 mb-2">Storico divisori {loading && "· caricamento..."}</div>
            {rows.length === 0 ? (
              <div className="text-sm text-slate-500 italic">Nessun divisore configurato. La percentuale part-time non potrà essere calcolata automaticamente per i dipendenti di questo CCNL.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b">
                  <tr><th className="text-left py-1">Decorrenza</th><th className="text-left py-1">Fine validità</th><th className="text-right py-1">Div. orario</th><th className="text-right py-1">Div. giorn.</th><th className="text-left py-1">Note</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100">
                      <td className="py-1">{r.effective_from}</td>
                      <td className="py-1 text-slate-500">{r.effective_to || <span className="text-emerald-700">corrente</span>}</td>
                      <td className="py-1 text-right font-mono">{Number(r.divisore_orario_mensile).toFixed(2).replace(".", ",")}</td>
                      <td className="py-1 text-right font-mono">{Number(r.divisore_giornaliero_mensile).toFixed(2).replace(".", ",")}</td>
                      <td className="py-1 text-slate-600">{r.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
